/**
 * UserPermissoesSection — UI-2 / TD-V2-PERM-02.
 *
 * Seção "Permissões" no UserFormDialog em modo edit. Não usa react-hook-form
 * principal porque mudanças são salvas via PUT separado quando user clica
 * "Salvar". Usa state local + chama useUserActions().updateUser quando user
 * fecha o form.
 *
 * Renderizada como controlled component — caller (UserFormDialog) passa
 * value e onChange.
 */

import { Alert, Label, Select, Switch } from '@/components/ui';
import type { User } from '@/lib/admin-types';

export interface UserPermissoesValue {
  podeCriarTarefaPraOutros: boolean;
  podeAgendarSemAprovacao: boolean;
  aprovadorId: string | null;
  /** FEAT-V2-035: permite inativar/reativar cards. Admin recebe true automatic
   *  via Migration 051 backfill — checkbox aqui só visível pra users não-admin. */
  podeInativarCards: boolean;
  /**
   * FEAT-V2-036 (Migration 059, 2026-05-25): permissão pra ver tarefas
   * atribuídas a outros usuários. Default = true (vê tudo). Admin SEMPRE
   * override (matheus continua super-admin) — toggle só visível pra non-admin.
   *
   * Sprint 2 (FEAT-V2-037 Fase A) vai override por perfil: Comercial e
   * Técnico → false, Administrativo → true.
   */
  podeVerTarefasOutros: boolean;
}

interface UserPermissoesSectionProps {
  value: UserPermissoesValue;
  onChange: (next: UserPermissoesValue) => void;
  /** Lista de users disponíveis pra ser aprovador (exceto o próprio user editado). */
  candidatosAprovador: User[];
  /** Se true (user editado é admin), esconde checkbox podeInativarCards
   *  (admin sempre tem). */
  isEditingAdmin?: boolean;
}

export function UserPermissoesSection({
  value,
  onChange,
  candidatosAprovador,
  isEditingAdmin = false,
}: UserPermissoesSectionProps) {
  const aprovadorObrigatorio = !value.podeAgendarSemAprovacao;
  const aprovadorVazio = aprovadorObrigatorio && !value.aprovadorId;

  return (
    <div data-testid="user-permissoes-section" className="min-w-0 space-y-3">
      <p className="eyebrow">Permissões de tarefas</p>

      <Switch
        checked={value.podeCriarTarefaPraOutros}
        onChange={(e) =>
          onChange({ ...value, podeCriarTarefaPraOutros: e.target.checked })
        }
        label="Pode criar tarefa pra outros colaboradores"
        data-testid="perm-criar-pra-outros"
      />

      <Switch
        checked={value.podeAgendarSemAprovacao}
        onChange={(e) => {
          const checked = e.target.checked;
          onChange({
            ...value,
            podeAgendarSemAprovacao: checked,
            // Se ligou agendar sem aprovação, aprovadorId vira opcional (mantém valor pra rollback)
            aprovadorId: checked ? null : value.aprovadorId,
          });
        }}
        label="Pode agendar tarefa sem precisar de aprovação"
        data-testid="perm-agendar-sem-aprovacao"
      />

      {aprovadorObrigatorio && (
        <div className="min-w-0">
          <Label htmlFor="perm-aprovador" required={aprovadorObrigatorio}>
            Aprovador
          </Label>
          <Select
            id="perm-aprovador"
            value={value.aprovadorId ?? ''}
            onChange={(e) =>
              onChange({ ...value, aprovadorId: e.target.value || null })
            }
            data-testid="perm-aprovador"
          >
            <option value="">Selecione um aprovador…</option>
            {candidatosAprovador.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome} {u.isAdmin ? '(admin)' : ''}
              </option>
            ))}
          </Select>
          {aprovadorVazio && (
            <Alert variant="warning" rail className="mt-1 text-xs">
              Aprovador obrigatório enquanto &quot;agendar sem aprovação&quot; estiver desligado.
            </Alert>
          )}
        </div>
      )}

      {/* FEAT-V2-035: visível só pra non-admin. Admin SEMPRE pode inativar
          cards (Migration 051 backfill). */}
      {!isEditingAdmin && (
        <Switch
          checked={value.podeInativarCards}
          onChange={(e) =>
            onChange({ ...value, podeInativarCards: e.target.checked })
          }
          label="Pode arquivar e reativar cards do quadro"
          data-testid="perm-inativar-cards"
        />
      )}

      {/* FEAT-V2-036 (Migration 059, 2026-05-25): visível só pra non-admin.
          Admin SEMPRE override no backend (helper userPodeVerTarefasOutros
          é chamado apenas após `!is_admin`). Default migration = true. */}
      {!isEditingAdmin && (
        <Switch
          checked={value.podeVerTarefasOutros}
          onChange={(e) =>
            onChange({ ...value, podeVerTarefasOutros: e.target.checked })
          }
          label="Pode ver as tarefas de outras pessoas"
          hint="Desligado, a pessoa só enxerga as tarefas dela — na agenda e dentro dos cards. Quem é administrador vê todas."
          data-testid="perm-ver-tarefas-outros"
        />
      )}
    </div>
  );
}
